<template>
  <div>
    <h2>{{ messages.coverage_change_request_title }} {{ licensePlate }}</h2>
    <p>{{ messages.coverage_change_request_description }}</p>

    <div class="spinner-wrapper" v-if="loading">
      <span class="spinner"></span>
    </div>

    <div v-if="error">
      {{ messages.coverage_change_request_general_error }}
    </div>

    <form v-if="!loading && !error">
      <vue-form-generator
        ref="vfg"
        :schema="schema"
        :model="model"
        :options="formOptions"
        @model-updated="onModelUpdated"
        @validated="onValidated"
      ></vue-form-generator>

      <div class="button-group">
        <a
          class="btn btn-primary-inverse"
          href="javascript:window.history.back()"
          >{{ messages.coverage_change_request_button_cancel }}</a
        >
        <input
          type="button"
          class="btn btn-primary"
          @click.prevent="submitClicked"
          :disabled="!formValid"
          :value="messages.coverage_change_request_button_submit"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {Api as CoreApi, Utils} from "@bvm/core";
import * as Api from "./coverage-change-request-form.api";
import {getSchemaForType} from "./formSchemas/form-schemas-factory";
import modalComponent from "./coverage-change-request-form-modal";

export default {
  name: "coverage-change-request-form",
  props: {
    insuredVehicleId: Number
  },
  data() {
    return {
      messages: {},
      messageKeys: [
        "coverage_change_request_title",
        "coverage_change_request_description",
        "coverage_change_request_general_error",
        "coverage_change_request_button_cancel",
        "coverage_change_request_button_submit",
        "coverage_change_request_form_group_primary",
        "coverage_change_request_form_group_additional",
        "coverage_change_request_validation_casco_error",
        // Help
        "coverage_change_request_form_dekVoertuigWA_help",
        "coverage_change_request_form_dekVoertuigBeperktCasco_help",
        "coverage_change_request_form_dekVoertuigCasco_help",
        "coverage_change_request_form_dekVoertuigOngevallenInzittenden_help",
        "coverage_change_request_form_dekVoertuigSchadeInzittenden_help",
        "coverage_change_request_form_dekVoertuigVerhaalsrechtsbijstand_help",
        "coverage_change_request_form_dekVoertuigVervangendVervoer_help",
        "coverage_change_request_form_dekVoertuigVerhaalrechtsbijstand_help",
        "coverage_change_request_form_dekInventarisMobieleWerkplaats_help"
      ],
      loading: true,
      error: undefined,
      formValid: true,
      insuredVehicle: undefined,

      model: {},
      schema: {},
      formOptions: {
        validateAfterLoad: true,
        validateAfterChanged: true
      }
    };
  },
  computed: {
    licensePlate: function() {
      return (this.insuredVehicle || {}).licensePlate;
    }
  },
  created() {
    const self = this;

    let insuredVehicleId = Utils.Url.getQueryParameters().insuredVehicleId || this.insuredVehicleId;

    Promise.all([
      CoreApi.Lang.bulk(CoreApi.Lang.NL, this.messageKeys),
      this.loadInsuredVehicle(insuredVehicleId)
    ])
      .then(([messages, insuredVehicle]) => {
        self.messages = messages;

        return this.getCoverageTypes(insuredVehicle.vehicleType.code).then((coverageTypes) => {
          return getSchemaForType(messages, coverageTypes, insuredVehicle.vehicleType.code).then(resolvedSchema => {
            self.insuredVehicle = insuredVehicle;
            self.schema = resolvedSchema;
            self.parseInitialActiveCoverages();
          }, self.handleError);
        });
      })
      .catch(self.handleError)
      .finally(() => this.loading = false);
  },
  methods: {
    onModelUpdated() {
      this.$refs.vfg.validate();
    },
    onValidated(isValid) {
      this.formValid = isValid;
    },
    handleError(error) {
      this.error = true;
      console.warn(error);
    },
    getCoverageTypes(vehicleType) {
      return Api.getCoverageTypes(vehicleType).then(response => response.data);
    },
    loadInsuredVehicle(insuredVehicleId) {
      return Api.getInsuredVehicle(insuredVehicleId).then(
        response => response.data
      );
    },
    parseInitialActiveCoverages() {
      this.insuredVehicle.coverages.forEach(coverage => {
        this.model[coverage.code] = true;
      });
    },
    submitClicked() {
      if (!this.formValid) {
        return;
      }

      let coverageCodes = [];

      for (let code in this.model) {
        if (this.model[code]) {
          coverageCodes.push(code);
        }
      }

      Bovemij.EventBus.$emit(Bovemij.Events.OPEN_MODAL, {
        component: modalComponent,
        props: {
          insuredVehicleId: this.insuredVehicle.id,
          coverageCodes: coverageCodes
        }
      });
    }
  }
};
</script>

<style lang="scss">
.vue-form-generator {
  $icon-info-outline: "\e94d";

  fieldset {
    border: 0;
    padding: 10px 8px 8px 0;

    legend {
      font-weight: bold;
      font-size: 1.2em;
    }
  }

  .form-group {
    display: flex;
    justify-content: flex-start;

    label {
      position: relative;
      display: flex;
      align-items: center;
      min-height: 24px;
      cursor: pointer;
      justify-content: space-between;
      width: 450px;
      order: 1;

      .help {
        .helpText {
          display: none;
          position: absolute;
          bottom: 30px;
          min-width: 200px;
          padding: 1rem;
          background-color: #fff;
          box-shadow: 5px 5px 10px 1px rgba(0, 0, 0, 0.5);
          border-radius: 0;
          color: #1a1a1a;
          font-size: 0.875rem;
          text-align: left;
        }

        .icon {
          font-size: 1.2em;
          color: gray;

          &:before {
            font-family: bovemij-icons !important;
            content: $icon-info-outline;
          }

          &:hover + .helpText {
            display: block;
          }
        }
      }
    }

    .field-wrap {
      order: 0;
    }

    .errors {
      order: 2;
      margin: 2px 10px;
    }
  }
}

.button-group {
  input[type="button"],
  a {
    &:not(:last-child) {
      margin-right: 20px;
    }
  }
}
</style>
